import { useDataCollection } from 'modules/AnalyticsCollection/AnalyticsCollectionProvider';
import { useCreateApplicationModal } from 'modules/Apps/hooks';
import { useCurrentUser } from 'modules/Auth/hooks';
import { useCurrentStore } from 'modules/Listings/store/useCurrentStore';
import { useRouter } from 'next/router';
import { IconButton } from 'components/Button';
import { hideBelowTab, navBarDropdown, navLink__Active, navLink__Radius } from 'components/Navbar/Navbar.styles';
import { IconCloud, IconCube, IconDropArrowDown, IconOrganization, IconPlus, IconRocket02, IconRunner } from 'components/Icons';
import { Popover, PopoverAction, usePopoverState } from 'components/Popover';
import { cx } from '@linaria/core';
import { NewBadge } from 'components/SidebarV2/SidebarContent';
import { useFeaturedFlags } from 'modules/FeaturedFlags/useFeaturedFlags';
import { computeDocumentation } from 'modules/Compute/utils';
import { CreateNewOrgButton } from '../AccountSelector/CreateNewOrgButton';
import { PopoverButton, PopoverLink, PopoverSection } from '../PopoverLayout';
import { NavLink } from '../NavLink';

export const CreateButton: React.FC = () => {
  const router = useRouter();
  const currentUser = useCurrentUser();
  const { track } = useDataCollection();
  const { load } = useCurrentStore();
  const [hasOrgAccess] = useFeaturedFlags(['OrgCreateOrganization']);
  const popoverState = usePopoverState({ placement: 'bottom-end' });

  const onHeaderBtnClick = (btnText: string): void => {
    track('Navbar Button Clicked', { text: btnText, context: 'regular' });
  };

  const { canCreateApplication, openCreateApplicationModal } = useCreateApplicationModal({
    onAppCreated: (createdApp, fromTemplate = false) => {
      load();
      router.push(`/${createdApp.userId}/${createdApp.appId}${!fromTemplate ? '?show_wizard_model_path_modal=true' : ''}`);
      track('User App Created', { context: 'regular' });
    },
  });

  const handleCreateApp = (): void => {
    onHeaderBtnClick('create_app');
    openCreateApplicationModal();
  };

  return (
    <>
      <PopoverAction popover={popoverState} className={hideBelowTab}>
        <IconButton
          key="notifications-popover"
          aria-label="Create new..."
          variant="tertiary"
          size={20}
          data-testid="show-notifications-btn"
          Icon={(iconProps) => (
            <>
              <IconPlus {...iconProps} />
              <IconDropArrowDown size={20} style={{ marginLeft: '0.25rem' }} />
            </>
          )}
          tooltip="Create new..."
          className={cx(navBarDropdown, navLink__Active, navLink__Radius, popoverState.visible && 'active')}
        />
      </PopoverAction>

      <Popover popover={popoverState} data-testid="create-new-popover" style={{ padding: '0.5rem 0' }}>
        <PopoverSection>
          {canCreateApplication && (
            <NavLink href={`/${currentUser?.user_id}/apps/new`}>
              <PopoverButton
                key="create-an-app"
                text="New Application"
                icon={IconCube}
                onClick={(e) => {
                  e.preventDefault();
                  handleCreateApp();
                }}
              />
            </NavLink>
          )}
          {hasOrgAccess && (
            <CreateNewOrgButton
              onClose={popoverState.hide}
              customButton={(props) => <PopoverButton key="create-an-org" text="New Organization" icon={IconOrganization} {...props} />}
            />
          )}
        </PopoverSection>

        <PopoverSection>
          <PopoverLink key="create-a-cluster" text="New Compute Cluster" icon={IconCloud} href={`/${currentUser?.user_id}/compute/create`} />
          <PopoverLink key="deploy-a-model" text="Deploy a Model" icon={IconRocket02} href={`/${currentUser?.user_id}/compute/deployments/create`} />
          <PopoverLink
            key="create-a-local-runner"
            text="New Local Runner"
            icon={IconRunner}
            badge={<NewBadge />}
            href={computeDocumentation.localRunnersQuickstart(currentUser?.user_id)}
          />
        </PopoverSection>
      </Popover>
    </>
  );
};
